import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { isNativePlatform } from '@/renderer/utils/isNativePlatform';

/**
 * Persistent, low-key app-awareness card near the top of the sidebar. Unlike a
 * dismissible banner, this is always present so members on ANY platform — desktop
 * web included — know the native apps exist. Hidden inside the installed native
 * app (nothing to promote once you're in it).
 *
 * Links to the /app landing page (store links + Telegram, iOS "coming soon").
 *
 * `variant="page"` is the light/dark app-theme card at the top of the phone
 * /more page (#2142), where the sidebar (and so its copy of this card) is
 * hidden. Push adoption depends on members finding the app, so it stays near
 * the top there too.
 */
export default function AppPromoCard({
  variant = 'sidebar',
}: { variant?: 'sidebar' | 'page' } = {}) {
  // Client-only reveal: isNativePlatform / hydration. Render nothing on the
  // server and until we've confirmed we're not inside the native app, so we
  // never flash it to native users or trip a hydration mismatch.
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isNativePlatform()) {
      setShow(true);
    }
  }, []);

  if (!show) {
    return null;
  }

  return (
    <a
      href="/app"
      className={twMerge(
        'flex items-center gap-2.5 rounded-lg bg-emerald-400/10 px-3 py-1.5 text-sm ring-1 ring-emerald-400/20 transition hover:bg-emerald-400/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400',
        variant === 'page' &&
          'rounded-2xl bg-emerald-50 px-4 py-3 no-underline ring-emerald-600/20 active:bg-emerald-100 dark:bg-emerald-400/10 dark:ring-emerald-400/20 dark:active:bg-emerald-400/15',
      )}
    >
      <DevicePhoneMobileIcon
        className={twMerge(
          'size-5 shrink-0 text-emerald-300',
          variant === 'page' && 'size-6 text-emerald-700 dark:text-emerald-300',
        )}
        aria-hidden="true"
      />
      <span className="min-w-0">
        <span
          className={twMerge(
            'block font-semibold text-emerald-100 leading-tight',
            variant === 'page' && 'text-emerald-900 dark:text-emerald-100',
          )}
        >
          Get the RAS app
        </span>
        <span
          className={twMerge(
            'block text-xs text-zinc-400',
            variant === 'page' && 'text-emerald-800/80 dark:text-zinc-400',
          )}
        >
          Android now live · iOS coming soon
        </span>
      </span>
    </a>
  );
}
